import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  View,
  Text,
  Pressable,
  ScrollView,
  StyleSheet,
  ActivityIndicator,
  Modal,
  Linking,
  useWindowDimensions,
} from "react-native";
import { useNavigation, useFocusEffect } from "@react-navigation/native";
import { Feather } from "@expo/vector-icons";
import Header from "../../../components/Header";
import AdminAlert from "../../../components/AdminAlert";
import { SectionCard } from "../Products/formStyles";
import { ADMIN_COLORS, ADMIN_FONT } from "../../../config/theme/adminColors";
import AdminSwitch from "../../../components/AdminSwitch";
import {
  getVisibleKeys,
  getPremiumOnlyKeys,
  loadPlanType,
  loadFeatureFlags,
  saveFeatureFlags,
} from "./service/features-service";
import {
  useCatalogProducts,
  useCatalogProductsSnapshot,
} from "../../../queries/useCatalogProducts";
import {
  getCatalogVitrineMissingPublishedModuleNames,
  getCatalogVitrineReadiness,
  getCatalogVitrineReadinessIssues,
} from "../../../utils/catalogVitrineReadiness";
import { getPublishedCatalogCards } from "../../../utils/productCatalog";
import { useCategories } from "../../../queries/useCategories";
import {
  CatalogVitrineReadinessBanner,
  CATALOG_VITRINE_ACTIVE_WARNING_TITLE,
  CATALOG_VITRINE_ENABLE_BLOCKED_TITLE,
} from "../../../components/CatalogVitrineReadinessBanner";
import { Provider } from "react-native-paper";
import { useAuthUser } from "../../../contexts/useAuthUser";

const LABELS = {
  hasPlanner: "Calendário Semanal - Planner",
  hasScheduleNotifications: "Notificações agendadas",
  hasGeneralNotifications: "Notificações gerais",
  hasComments: "Comentários",
  hasProgress: "Acompanhamento de Progresso do Aluno",
  hasProfileInfos: "Perfil Completo do Aluno",
  hasCommunity: "Comunidade",
  hasForm: "Formulário PAR-Q",
  hasDownload: "Download para assistir offline",
  blockDownloadPdf: "Bloquear download PDF",
  blockScreenCapture: "Bloquear captura e gravação de tela",
  hasProductCatalog: "Vitrine de Produtos",
};

const DESCRIPTIONS = {
  hasPlanner:
    "Permita que seus alunos(as) organizem a rotina de práticas semanais e criem alertas personalizados.",
  hasScheduleNotifications:
    "Envie notificações automáticas em horários específicos para lembrar seus alunos de treinos, aulas ou atividades.",
  hasGeneralNotifications:
    "Envie comunicados e atualizações para todos os alunos diretamente pelo app.",
  hasComments:
    "O aluno e você poderão interagir entre si através de comentários nas páginas de conteúdo.",
  hasProgress:
    "Permita que o aluno(a) registre seu peso corporal e fotos de evolução.",
  hasProfileInfos:
    "Habilitar o preenchimento de dados extras no perfil: gênero, data de aniversário e data da última mestruação.",
  hasCommunity:
    "Um espaço exclusivo para interação para alunos(as) cadastrados no app, fortalecendo o senso de pertencimento.",
  hasForm:
    "Habilite o preenchimento de formulário (Google Forms) obrigatório para que o(a) aluno(a) possa acessar os conteúdos. PAR-Q: autoavaliação que identifica riscos à saúde e a necessidade de avaliação médica antes de iniciar exercícios.",
  hasDownload:
    "Permitir que façam download de vídeos para assistir offline no aplicativo mobile.",
  blockDownloadPdf: "Bloquear que façam download de PDFs no aplicativo mobile.",
  blockScreenCapture:
    "Impede prints e gravações de tela dos seus conteúdos no aplicativo mobile. Vem ativado por segurança — desative apenas se você quiser permitir que os alunos capturem a tela.",
  hasProductCatalog:
    "Exibe na página principal uma vitrine com os produtos, planos e cursos disponíveis, com acesso rápido ao conteúdo de cada um. Só pode ser ativada quando todos os produtos visíveis na vitrine tiverem imagem de capa e categoria configuradas na aba Produtos.",
};

const NOTIFICATION_KEYS = [
  "hasScheduleNotifications",
  "hasGeneralNotifications",
];

const NOTIFICATION_WHATSAPP_MESSAGES = {
  hasScheduleNotifications:
    "Olá! Ativei as *Notificações Agendadas* no painel e gostaria de finalizar a configuração.",
  hasGeneralNotifications:
    "Olá! Ativei as *Notificações Gerais* no painel e gostaria de finalizar a configuração.",
};

function FeatureRow({
  featureKey,
  value,
  onToggle,
  disabled,
  isSubFeature,
  showDivider,
}) {
  return (
    <View
      style={[
        styles.featureRow,
        isSubFeature && styles.subFeature,
        showDivider && styles.featureDivider,
      ]}
    >
      <View style={{ flex: 1, paddingRight: 16 }}>
        <Text style={styles.featureLabel}>{LABELS[featureKey]}</Text>
        {DESCRIPTIONS[featureKey] ? (
          <Text style={styles.featureDesc}>{DESCRIPTIONS[featureKey]}</Text>
        ) : null}
      </View>
      <AdminSwitch
        value={value}
        onValueChange={() => onToggle(featureKey)}
        disabled={disabled}
        accent={ADMIN_COLORS.teal}
      />
    </View>
  );
}

export function Features() {
  const navigation = useNavigation();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const [planType, setPlanType] = useState(null);
  const [booleans, setBooleans] = useState({});
  const [initialBooleans, setInitialBooleans] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [dismissedEnableBlocked, setDismissedEnableBlocked] = useState(false);
  const [dismissedActiveWarning, setDismissedActiveWarning] = useState(false);
  const [notificationModalKey, setNotificationModalKey] = useState(null);
  const { user } = useAuthUser();
  const { data: catalogProducts = [], refetch: refetchCatalogProducts } =
    useCatalogProducts(true);
  useCatalogProductsSnapshot(true);

  const { data: allCategories, refetch: refetchCategories } = useCategories();

  useFocusEffect(
    useCallback(() => {
      refetchCatalogProducts();
      refetchCategories();
    }, [refetchCatalogProducts, refetchCategories])
  );

  const vitrineActiveWarning = useMemo(() => {
    const readiness = getCatalogVitrineReadiness(catalogProducts);
    if (readiness.ready) return null;
    return getCatalogVitrineReadinessIssues(readiness);
  }, [catalogProducts]);

  // Warning only (does not block enabling): products ready for the showcase
  // whose linked modules are all archived/hidden, so students don't see them.
  const missingPublishedModuleNames = useMemo(() => {
    if (!allCategories) return [];
    return getCatalogVitrineMissingPublishedModuleNames(
      catalogProducts,
      getPublishedCatalogCards(allCategories)
    );
  }, [catalogProducts, allCategories]);

  useEffect(() => {
    setDismissedEnableBlocked(false);
    setDismissedActiveWarning(false);
  }, [vitrineActiveWarning, missingPublishedModuleNames]);

  useEffect(() => {
    initializeData();
  }, []);

  // As duas leituras são independentes: uma falha no plano não pode esconder as
  // funcionalidades (e vice-versa). O erro também vai para o console e o código
  // dele aparece no alerta — antes o `catch {}` engolia tudo e a única pista era
  // "não foi possível carregar", sem dizer o quê.
  const initializeData = async () => {
    const failures = [];

    let plan;
    try {
      plan = await loadPlanType();
    } catch (error) {
      console.error("Erro ao carregar o plano do cliente:", error);
      failures.push(error);
    }

    try {
      const flags = await loadFeatureFlags(plan);
      setBooleans(flags);
      setInitialBooleans(flags);
    } catch (error) {
      console.error("Erro ao carregar as funcionalidades:", error);
      failures.push(error);
    }

    setPlanType(plan ?? null);

    if (failures.length) {
      const detail = failures[0]?.code || failures[0]?.message;
      setToast({
        variant: "error",
        message: detail
          ? `Não foi possível carregar as configurações. (${detail})`
          : "Não foi possível carregar as configurações.",
      });
    }

    setLoading(false);
  };

  const validateProductCatalogEnable = useCallback(async () => {
    const { data: freshProducts } = await refetchCatalogProducts();
    const products = freshProducts ?? catalogProducts;
    return getCatalogVitrineReadiness(products).ready;
  }, [refetchCatalogProducts, catalogProducts]);

  const handleSave = async () => {
    setSaving(true);
    setToast(null);

    const hasFlagChanges =
      JSON.stringify(booleans) !== JSON.stringify(initialBooleans);

    try {
      if (hasFlagChanges) {
        if (booleans.hasProductCatalog && !initialBooleans.hasProductCatalog) {
          const canEnable = await validateProductCatalogEnable();
          if (!canEnable) return;
        }

        const result = await saveFeatureFlags(booleans);
        if (!result?.success) {
          setToast({
            variant: "error",
            message: "Erro ao salvar as funcionalidades. Tente novamente.",
          });
          return;
        }

        // Flags já foram persistidas — atualiza baseline antes da vitrine,
        // senão reativar o toggle deixa hasChanges=false e trava o Salvar.
        setInitialBooleans({ ...booleans });
      }

      setToast({
        variant: "success",
        message: "Configurações salvas com sucesso.",
      });
    } catch (error) {
      const message = error?.message?.includes("permission-denied")
        ? "Somente administradores podem alterar as configurações."
        : "Erro ao salvar as configurações. Tente novamente.";
      setToast({ variant: "error", message });
    } finally {
      setSaving(false);
    }
  };

  const toggleBoolean = async (key) => {
    setToast(null);

    if (key === "hasProductCatalog" && !booleans[key]) {
      try {
        const canEnable = await validateProductCatalogEnable();
        if (!canEnable) return;
      } catch (error) {
        console.error("Erro ao validar produtos da vitrine:", error);
        setToast({
          variant: "error",
          message: "Não foi possível verificar os produtos da vitrine.",
        });
        return;
      }
    }

    setBooleans((prev) => {
      const newValue = !prev[key];
      if (NOTIFICATION_KEYS.includes(key) && newValue)
        setNotificationModalKey(key);
      const updates = { [key]: newValue };
      if (key === "hasPlanner" && !newValue)
        updates.hasScheduleNotifications = false;
      return { ...prev, ...updates };
    });
  };

  const handleWhatsAppRedirect = () => {
    const phone = "5551998596019";
    const message = encodeURIComponent(
      NOTIFICATION_WHATSAPP_MESSAGES[notificationModalKey] || "",
    );
    Linking.openURL(`https://wa.me/${phone}?text=${message}`);
    setNotificationModalKey(null);
  };

  const hasChanges = useMemo(
    () => JSON.stringify(booleans) !== JSON.stringify(initialBooleans),
    [booleans, initialBooleans],
  );

  // Monta a lista plana de linhas (incluindo a sub-feature do Planner) para controlar
  // corretamente os divisores entre itens.
  const buildRows = (keys, isDisabled) => {
    const rows = [];
    keys.forEach((key) => {
      rows.push({ key, disabled: isDisabled, sub: false });
      if (key === "hasPlanner" && (isDisabled || booleans.hasPlanner)) {
        rows.push({
          key: "hasScheduleNotifications",
          disabled: isDisabled,
          sub: true,
        });
      }
    });
    return rows;
  };

  const renderRows = (keys, isDisabled = false) => {
    const rows = buildRows(keys, isDisabled);
    const elements = [];

    rows.forEach((row, i) => {
      const isProductCatalog = row.key === "hasProductCatalog";
      const hasCatalogIssues =
        (vitrineActiveWarning?.missingPhotoProductNames?.length ?? 0) > 0 ||
        (vitrineActiveWarning?.missingCategoryProductNames?.length ?? 0) > 0;
      const showEnableBlockedBanner =
        isProductCatalog &&
        !booleans.hasProductCatalog &&
        hasCatalogIssues &&
        !dismissedEnableBlocked;
      const showActiveWarningBanner =
        isProductCatalog &&
        booleans.hasProductCatalog &&
        initialBooleans.hasProductCatalog &&
        (hasCatalogIssues || missingPublishedModuleNames.length > 0) &&
        !dismissedActiveWarning &&
        !showEnableBlockedBanner;
      const showAnyBanner = showEnableBlockedBanner || showActiveWarningBanner;

      elements.push(
        <FeatureRow
          key={row.key}
          featureKey={row.key}
          value={row.disabled ? false : (booleans[row.key] ?? false)}
          onToggle={row.disabled ? () => {} : toggleBoolean}
          disabled={row.disabled}
          isSubFeature={row.sub}
          showDivider={!showAnyBanner && i < rows.length - 1}
        />
      );

      if (showEnableBlockedBanner) {
        elements.push(
          <CatalogVitrineReadinessBanner
            key="catalog-vitrine-readiness"
            title={CATALOG_VITRINE_ENABLE_BLOCKED_TITLE}
            missingPhotoProductNames={vitrineActiveWarning.missingPhotoProductNames}
            missingCategoryProductNames={vitrineActiveWarning.missingCategoryProductNames}
            onClose={() => setDismissedEnableBlocked(true)}
          />
        );
      }

      if (showActiveWarningBanner) {
        elements.push(
          <CatalogVitrineReadinessBanner
            key="catalog-vitrine-active-warning"
            title={CATALOG_VITRINE_ACTIVE_WARNING_TITLE}
            missingPhotoProductNames={vitrineActiveWarning?.missingPhotoProductNames}
            missingCategoryProductNames={vitrineActiveWarning?.missingCategoryProductNames}
            missingPublishedModuleProductNames={missingPublishedModuleNames}
            onClose={() => setDismissedActiveWarning(true)}
          />
        );
      }
    });

    return elements;
  };

  const isPremium = planType === "premium";
  const canSave = hasChanges && !saving;

  return (
    <Provider>
      <View style={{ flex: 1, backgroundColor: ADMIN_COLORS.surfaceSubtle }}>
        <Header navigation={navigation} user={user} isAdminPage />

        <ScrollView contentContainerStyle={styles.scrollContent}>
          <View style={styles.page}>
            {/* Cabeçalho da página */}
            <View style={styles.headerBlock}>
              <View style={styles.headerTop}>
                <Text style={[styles.title, isMobile && { fontSize: 24 }]}>
                  Funcionalidades
                </Text>
                {!loading && planType ? (
                  <View
                    style={[
                      styles.planPill,
                      {
                        backgroundColor: isPremium
                          ? ADMIN_COLORS.successSoft
                          : ADMIN_COLORS.warnBg,
                      },
                    ]}
                  >
                    <Text
                      style={[
                        styles.planPillText,
                        {
                          color: isPremium
                            ? ADMIN_COLORS.success
                            : ADMIN_COLORS.warnText,
                        },
                      ]}
                    >
                      Plano {planType}
                    </Text>
                  </View>
                ) : null}
              </View>
              <Text style={styles.subtitle}>
                Ative ou desative os recursos disponíveis no seu aplicativo.
              </Text>
            </View>

            {loading ? (
              <View style={styles.loadingBox}>
                <ActivityIndicator color={ADMIN_COLORS.teal} size="large" />
              </View>
            ) : (
              <>
                <SectionCard
                  title="Funcionalidades"
                  subtitle="Recursos incluídos no seu plano atual."
                >
                  {renderRows(getVisibleKeys(planType))}
                </SectionCard>

                {planType === "growth" && (
                  <SectionCard
                    title="Funcionalidades Premium"
                    subtitle="Disponíveis ao fazer upgrade para o plano Premium."
                  >
                    <AdminAlert
                      variant="warn"
                      style={{ marginTop: 0, marginBottom: 4 }}
                    >
                      Faça upgrade para o plano Premium para desbloquear estes
                      recursos.
                    </AdminAlert>
                    <View style={{ opacity: 0.55 }}>
                      {renderRows(getPremiumOnlyKeys(), true)}
                    </View>
                  </SectionCard>
                )}

                {hasChanges ? (
                  <AdminAlert variant="warn">
                    Você possui alterações não salvas. Clique em "Salvar" para
                    aplicar.
                  </AdminAlert>
                ) : null}

                {toast ? (
                  <AdminAlert
                    variant={toast.variant}
                    onClose={() => setToast(null)}
                  >
                    {toast.message}
                  </AdminAlert>
                ) : null}

                <View style={styles.saveRow}>
                  <Pressable
                    onPress={handleSave}
                    disabled={!canSave}
                    style={({ hovered }) => [
                      styles.primaryBtn,
                      !canSave && { opacity: 0.5 },
                      canSave &&
                        hovered && { backgroundColor: ADMIN_COLORS.accentDark },
                    ]}
                  >
                    {saving ? (
                      <ActivityIndicator size="small" color="#FFFFFF" />
                    ) : (
                      <Text style={styles.primaryBtnText}>Salvar</Text>
                    )}
                  </Pressable>
                </View>
              </>
            )}
          </View>
        </ScrollView>

        {/* Modal de configuração de notificações */}
        <Modal
          visible={!!notificationModalKey}
          animationType="fade"
          transparent
          onRequestClose={() => setNotificationModalKey(null)}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.modalCard}>
              <View style={styles.modalIcon}>
                <Feather
                  name="alert-triangle"
                  size={22}
                  color={ADMIN_COLORS.warnText}
                />
              </View>
              <Text style={styles.modalTitle}>Configuração necessária</Text>
              <Text style={styles.modalMessage}>
                Para que as{" "}
                {notificationModalKey === "hasScheduleNotifications"
                  ? "Notificações Agendadas"
                  : "Notificações Gerais"}{" "}
                funcionem corretamente, é necessário contato com nossa equipe.
              </Text>

              <Pressable
                onPress={handleWhatsAppRedirect}
                style={({ hovered }) => [
                  styles.whatsappBtn,
                  hovered && { backgroundColor: "#1EBE5B" },
                ]}
              >
                <Feather name="message-circle" size={16} color="#FFFFFF" />
                <Text style={styles.whatsappBtnText}>Falar no WhatsApp</Text>
              </Pressable>

              <Pressable
                onPress={() => setNotificationModalKey(null)}
                style={styles.modalCloseBtn}
              >
                <Text style={styles.modalCloseText}>Fechar</Text>
              </Pressable>
            </View>
          </View>
        </Modal>
      </View>
    </Provider>
  );
}

const styles = StyleSheet.create({
  scrollContent: {
    flexGrow: 1,
    backgroundColor: ADMIN_COLORS.surfaceSubtle,
    padding: 24,
    alignItems: "center",
  },
  page: {
    width: "100%",
    maxWidth: 820,
  },
  headerBlock: {
    marginBottom: 20,
  },
  headerTop: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  title: {
    fontFamily: ADMIN_FONT,
    fontSize: 28,
    fontWeight: "700",
    color: ADMIN_COLORS.ink,
  },
  subtitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    color: ADMIN_COLORS.muted,
    marginTop: 4,
  },
  planPill: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 999,
  },
  planPillText: {
    fontFamily: ADMIN_FONT,
    fontSize: 12,
    fontWeight: "700",
    textTransform: "capitalize",
  },
  loadingBox: {
    paddingVertical: 80,
    alignItems: "center",
    justifyContent: "center",
  },
  featureRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 14,
  },
  featureDivider: {
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.border,
  },
  subFeature: {
    paddingLeft: 16,
  },
  featureLabel: {
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    fontWeight: "600",
    color: ADMIN_COLORS.ink,
  },
  featureDesc: {
    fontFamily: ADMIN_FONT,
    fontSize: 12.5,
    lineHeight: 17,
    color: ADMIN_COLORS.muted,
    marginTop: 3,
  },
  saveRow: {
    marginTop: 4,
    marginBottom: 8,
  },
  primaryBtn: {
    height: 44,
    borderRadius: 10,
    paddingHorizontal: 24,
    backgroundColor: ADMIN_COLORS.teal,
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "flex-start",
    minWidth: 120,
    cursor: "pointer",
  },
  primaryBtnText: {
    fontFamily: ADMIN_FONT,
    fontSize: 15,
    fontWeight: "600",
    color: "#FFFFFF",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(17,24,39,0.45)",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  modalCard: {
    width: "100%",
    maxWidth: 400,
    backgroundColor: ADMIN_COLORS.surface,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    padding: 24,
    alignItems: "center",
    boxShadow: "0 12px 32px rgba(16,24,40,0.18)",
  },
  modalIcon: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: ADMIN_COLORS.warnBg,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 12,
  },
  modalTitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 17,
    fontWeight: "700",
    color: ADMIN_COLORS.ink,
    textAlign: "center",
  },
  modalMessage: {
    fontFamily: ADMIN_FONT,
    fontSize: 13.5,
    lineHeight: 20,
    color: ADMIN_COLORS.muted,
    textAlign: "center",
    marginTop: 8,
    marginBottom: 20,
  },
  whatsappBtn: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    backgroundColor: "#25D366",
    paddingVertical: 12,
    borderRadius: 10,
    width: "100%",
    cursor: "pointer",
  },
  whatsappBtnText: {
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    fontWeight: "700",
    color: "#FFFFFF",
  },
  modalCloseBtn: {
    paddingVertical: 10,
    paddingHorizontal: 24,
    marginTop: 6,
    cursor: "pointer",
  },
  modalCloseText: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    fontWeight: "600",
    color: ADMIN_COLORS.muted,
  },
});
